import * as React from 'react';
import { useEffect, useState } from 'react';
import type { StageKey } from '../lib/types';
import { formatDuration } from '../lib/eta';

// Bottom-right toast stack. Hand-rolled — no react-hot-toast / sonner dep
// to add. The toast bus is module-level so any module can call
// `toast.stageDone(...)` without prop-drilling a context; the rendered
// `<ExportToast />` should be mounted ONCE at app root (recommend in
// `App.tsx` near the `<Routes>` block) so a single subscriber owns the
// rendered surface.
//
// Visual contract (SCENEFORGE_OPTIMIZE.md §3d):
//   - Monospace text on dark zinc-900 bg, 1px red-600 left border.
//   - Slide-in from below on enter, fade-out on auto-dismiss after 4s.
//   - Stack max 3 visible — older toasts age out so the screen never piles.
//   - No bounce, no spring, no emoji.
//
// Public API (also exported as `toast`):
//   - `toast.stageDone({ stage, durationMs })`
//   - `toast.exportReady({ jobId, durationMs })` — renders as "project ready"
//     to match the V2 partner-facing terminology ("Project" replaced "Export"
//     across the UI per 2026-05-23 UX pass).

interface ToastBase {
  id: number;
  message: string;
  /** ms since epoch when the toast was queued — used by the auto-dismiss
   *  timer and by ordering when multiple toasts fire in the same tick. */
  ts: number;
}

interface ToastBus {
  stageDone(args: { stage: StageKey; durationMs: number }): void;
  exportReady(args: { jobId: string; durationMs: number }): void;
}

type ToastListener = (toast: ToastBase) => void;

const STAGE_LABEL: Record<StageKey, string> = {
  extract: 'Extract',
  clean_plate: 'Clean plate',
  environment: 'Environment',
  multi_mask: 'Masks',
  // V4 Simple: post-approval clean-plate JPEG split.
  extract_clean_plate: 'Extract plate',
  world_track: 'World camera',
  feature_tracks_export: 'Tracks export',
  car_pose: 'Car pose',
  track_compose: 'Compose',
  hdri: 'HDRI',
  package: 'Package',
  // V2 legacy alias.
  car_track: 'Camera track',
};

let nextId = 1;
const listeners = new Set<ToastListener>();

function emit(message: string): void {
  const toastItem: ToastBase = { id: nextId++, message, ts: Date.now() };
  for (const listener of listeners) listener(toastItem);
}

/** Module-level singleton — import from anywhere to fire a toast. */
export const toast: ToastBus = {
  stageDone({ stage, durationMs }) {
    const label = STAGE_LABEL[stage] ?? stage;
    emit(`${label} complete · ${formatDuration(durationMs)}`);
  },
  exportReady({ jobId, durationMs }) {
    // jobId isn't rendered in the toast body itself — that would crowd the
    // 1-line message in mono — but it's logged on the bus call so future
    // analytics / Sentry breadcrumbs can correlate.
    void jobId;
    emit(`SceneForge project ready · ${formatDuration(durationMs)}`);
  },
};

const MAX_VISIBLE = 3;
const DISMISS_MS = 4_000;
// Match the slide-out duration in the CSS below so the timer + the
// transition end at the same moment.
const EXIT_MS = 200;

interface RenderedToast extends ToastBase {
  /** Local UI state — drives the CSS classes for the slide-in/out. */
  phase: 'enter' | 'idle' | 'exit';
}

export default function ExportToast(): React.ReactElement {
  const [items, setItems] = useState<RenderedToast[]>([]);

  useEffect(() => {
    const listener: ToastListener = (incoming) => {
      setItems((prev) => {
        // Trim from the head so the stack never exceeds MAX_VISIBLE — the
        // oldest toast slides out, making room for the new one.
        const trimmed =
          prev.length >= MAX_VISIBLE
            ? prev.slice(prev.length - (MAX_VISIBLE - 1))
            : prev;
        return [...trimmed, { ...incoming, phase: 'enter' }];
      });
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  // Drive the enter → idle transition one frame after mount so the CSS
  // transition actually runs. requestAnimationFrame is the canonical way to
  // do this without depending on a layout-effect race.
  useEffect(() => {
    const entering = items.filter((t) => t.phase === 'enter');
    if (entering.length === 0) return;
    const raf = window.requestAnimationFrame(() => {
      setItems((prev) =>
        prev.map((t) => (t.phase === 'enter' ? { ...t, phase: 'idle' } : t)),
      );
    });
    return () => window.cancelAnimationFrame(raf);
  }, [items]);

  // Auto-dismiss after DISMISS_MS — start the exit transition, then drop
  // the item from state once EXIT_MS has elapsed.
  useEffect(() => {
    const idleIds = items.filter((t) => t.phase === 'idle').map((t) => t.id);
    if (idleIds.length === 0) return;
    const timeouts: number[] = [];
    for (const id of idleIds) {
      const dismissT = window.setTimeout(() => {
        setItems((prev) =>
          prev.map((t) => (t.id === id ? { ...t, phase: 'exit' } : t)),
        );
        const removeT = window.setTimeout(() => {
          setItems((prev) => prev.filter((t) => t.id !== id));
        }, EXIT_MS);
        timeouts.push(removeT);
      }, DISMISS_MS);
      timeouts.push(dismissT);
    }
    return () => {
      for (const t of timeouts) window.clearTimeout(t);
    };
  }, [items]);

  if (items.length === 0) return <></>;

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2"
    >
      {items.map((t) => (
        <div
          key={t.id}
          role="status"
          className={[
            'pointer-events-auto min-w-[18rem] max-w-[26rem] rounded-md',
            'border border-zinc-700 border-l-2 border-l-red-600 bg-zinc-900',
            'px-3 py-2 font-mono text-[11px] leading-[1.4] text-zinc-200 shadow-lg',
            // Transition both opacity and translate so we get a soft slide-up
            // on enter and a soft slide-down on exit.
            'transition-[opacity,transform] duration-[80ms] ease-out',
            t.phase === 'enter'
              ? 'opacity-0 translate-y-2'
              : t.phase === 'exit'
                ? 'opacity-0 translate-y-2'
                : 'opacity-100 translate-y-0',
          ].join(' ')}
        >
          {t.message}
        </div>
      ))}
    </div>
  );
}
